import { useCallback, useMemo } from 'react'
import { useActiveOrder } from '@/features/shop/state/SelectedActiveOrder/hooks'
import { useQueryCart } from '@/features/shop/services/Cart/queries'
import { type VariationParent } from '@/features/shop/services/Product/variationTypes'

export const useBasket = () => {
  const cart = useQueryCart().data
  const currentActiveOrder = useActiveOrder()

  const orderMaps = useMemo(() => {
    if (!currentActiveOrder?.id) return null

    const noteById = new Map<number, string>()
    const quantityById = new Map<number, number>()

    for (const { requested_product, notes } of currentActiveOrder.order_lines) {
      // skip custom products
      if (requested_product.isCustomProduct) continue

      noteById.set(requested_product.id, notes ?? '')
      quantityById.set(requested_product.id, requested_product.quantity)
    }

    return { quantityById, noteById }
  }, [currentActiveOrder])

  const getProductQuantity = useCallback(
    (product: { id: number; parent?: VariationParent | null }): number => {
      const { id, parent } = product
      // (order)
      // unlike cart, order lines have parent set to null for
      // variation products, so we rely on the caller's product.parent
      // to identify siblings via variation_products.
      if (orderMaps) {
        if (parent) {
          return parent.variation_products.reduce((sum, { product_id }) => {
            if (!product_id) return sum
            return sum + (orderMaps.quantityById.get(Number(product_id)) ?? 0)
          }, 0)
        }
        return orderMaps.quantityById.get(id) ?? 0
      }
      // (cart)
      // The parent object ultimately determines if a product is a grouped variation.
      // If there were a bug where product_id was missing on the parent object, this
      // function could aggregate the quantity of standard products in cart (bad), so
      // we also check for the existence of the id and default to individual quantity
      // if it's missing
      if (parent?.product_id) {
        return cart?.parentQuantityById[Number(parent.product_id)] ?? 0
      }
      // use id as key for products without parent
      return cart?.quantityById[id] ?? 0
    },
    [orderMaps, cart],
  )

  const getProductNote = useCallback(
    (product: { id: number }): string => {
      if (orderMaps) {
        return orderMaps.noteById.get(product.id) ?? ''
      }
      return cart?.noteById[product.id] ?? ''
    },
    [orderMaps, cart],
  )

  const getProductPromotionMetadata = useCallback(
    (product: { id: number }) =>
      cart?.shopping_cart_lines.find((p) => p.id === product.id)?.promotionMetadata,
    [cart],
  )

  return { getProductQuantity, getProductNote, getProductPromotionMetadata }
}
